import {
  DrawerBody,
  DrawerHeader,
  FormController,
  FormLabel,
  Input,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AddOnlineReceipt } from '../../../../api/oms/order-history';
import { onlineErrors } from '../../../../constants/receipt-error';
import { useCommonContext } from '../../../../hooks/useContexts';
import {
  IFormData,
  IOnlineReceiptError,
  IReceiptForm,
} from '../../../../models/purchase-history/add-receipt';
import {
  addSlash,
  makeOnlineRequestBody,
} from '../../../../utils/order-history';
import { setHOrderError } from '../../../../utils/purchase-history/analytics';
import { AddReceiptDrawerFooter } from './AddReceiptDrawerFooter';

export const OnlineReceiptForm = ({
  closeDrawer,
  setAlertState,
  setDrawerStepper,
  svocId,
}: IReceiptForm) => {
  const { userID } = useThdCustomer();
  const [inputError, setInputError] = useState({
    orderNumber: '',
    orderDate: '',
    email: '',
  });
  const [isLoading, setLoading] = useState(false);

  const { isMobile } = useCommonContext();

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
    setError,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      email: '',
      orderDate: '',
      orderNumber: '',
      tryAgain404: false,
      apiError: false,
    },
  });
  const watchEmail = watch('email');
  const watchOrderDate = watch('orderDate');
  const watchOrderNumber = watch('orderNumber');

  useEffect(() => {
    if (
      watchOrderNumber?.length === 10 &&
      watchOrderNumber?.match(/[W,w,C,c,H,h][\d,\w]\d{8}$/)
    ) {
      setInputError((prevState) => ({
        ...prevState,
        orderNumber: 'success',
      }));
      clearErrors('orderNumber');
    }
    if (
      inputError?.orderNumber === 'success' &&
      watchOrderNumber?.length !== 10
    ) {
      setInputError((prevState) => ({
        ...prevState,
        orderNumber: 'error',
      }));
      setError('orderNumber', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchOrderNumber]);

  useEffect(() => {
    if (watchOrderDate?.length === 8) {
      const tempYear = watchOrderDate?.split('/');
      const tempDate = new Date(
        `${tempYear[2] < '20' ? '19' : '20'}${tempYear[2]}/${tempYear[0]}/${
          tempYear[1]
        }`,
      );
      if (Number.isNaN(tempDate.getTime())) {
        setInputError((prevState) => ({
          ...prevState,
          orderDate: 'error',
        }));
        setError('orderDate', { type: 'invalid', message: 'invalid entry' });
        return;
      }
      setInputError((prevState) => ({
        ...prevState,
        orderDate: 'success',
      }));
      clearErrors('orderDate');
      return;
    }
    if (inputError?.orderDate === 'success' && watchOrderDate?.length !== 8) {
      setInputError((prevState) => ({
        ...prevState,
        orderDate: 'error',
      }));
      setError('orderDate', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchOrderDate]);

  useEffect(() => {
    if (watchEmail?.length > 6 && watchEmail?.includes('.')) {
      if (watchEmail?.includes('@')) {
        setInputError((prevState) => ({
          ...prevState,
          email: 'success',
        }));
        clearErrors('email');
      } else {
        setInputError((prevState) => ({
          ...prevState,
          email: 'error',
        }));
        setError('email', { type: 'invalid', message: 'invalid entry' });
      }
    }
    if (inputError?.email === 'success' && watchEmail?.length < 7) {
      setInputError((prevState) => ({
        ...prevState,
        email: 'error',
      }));
      setError('email', { type: 'invalid', message: 'invalid entry' });
    }
  }, [watchEmail]);

  const onFormSubmit: SubmitHandler<IFormData> = async ({
    email = '',
    orderDate = '',
    orderNumber = '',
  }) => {
    clearErrors();
    try {
      let request;
      if (email && orderDate && orderNumber && svocId) {
        setLoading(true);

        request = makeOnlineRequestBody(svocId, orderNumber, orderDate, email);

        const response = await AddOnlineReceipt(request, userID);
        const status = response ? response.status : null;

        if (status === null) {
          setError('tryAgain404', {
            type: 'failed',
            message: 'failed request',
          });
          return;
        }
        closeDrawer();
        reset();
        setAlertState({
          alertMessage: `You've successfully added an online receipt to your account. View Order ${orderNumber}`,
          status: 'success',
        });
      }
    } catch (error: unknown) {
      const hCode = 'B2B_ADD_ONLINE_RECEIPT_ERR_8309';
      const desc = (error as IOnlineReceiptError)?.errors?.error?.[0]
        ?.description;
      const errorDescription: string | undefined = (
        error as IOnlineReceiptError
      ).response?.data?.error?.[0]?.description;
      const code = (error as IOnlineReceiptError)?.errors?.error?.[0]?.code;
      if (code === hCode) {
        setHOrderError(desc ?? '');
        return;
      }
      if ((error as IOnlineReceiptError).response.status === 500) {
        setError('tryAgain404', {
          type: 'failed',
          message: 'failed request',
        });
        return;
      }
      let errorMessage;
      try {
        errorMessage = JSON.parse(errorDescription as string)
          ?.addReceiptResponse?.receipts?.[0]?.errors?.[0]?.description;
      } catch (err) {
        errorMessage =
          'We were unable to retrieve your receipt details. Refresh the page and try again.';
      }
      setError('apiError', {
        type: 'failed',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    clearErrors('tryAgain404');
    handleSubmit(onFormSubmit)();
  };

  return (
    <div
      data-testid="onlineReceiptForm"
      className="sui-flex sui-flex-col sui-h-full"
    >
      <DrawerHeader
        title="Add Online Receipt"
        onClose={closeDrawer}
        onBack={() => setDrawerStepper && setDrawerStepper(0)}
      />
      <DrawerBody style={{ height: isMobile ? '65vh' : '' }}>
        <Typography>
          Please enter the following details found in your order:
        </Typography>
        <FormController
          className="sui-mt-6"
          fullWidth
          status={inputError.orderNumber || (errors?.orderNumber && 'error')}
        >
          <FormLabel htmlFor="orderNumber">
            Order Number<sup>*</sup>
          </FormLabel>
          <Input
            aria-describedby="orderNumber-input"
            fullWidth
            id="orderNumber"
            inputProps={{
              maxLength: 10,
            }}
            {...register('orderNumber', {
              required: true,
              minLength: 9,
              pattern: /[W,w,C,c,H,h][\d,\w]\d{8}$/,
              onChange: (event: { target: { value: string } }) =>
                setValue('orderNumber', event.target.value.toUpperCase()),
              onBlur: (event: { target: { value: string } }) =>
                setValue('orderNumber', event.target.value.toUpperCase()),
            })}
            placeholder="W123456789"
          />
          {(inputError.orderNumber === 'error' || errors.orderNumber) && (
            <Typography variant="body-xs" color="danger">
              {errors?.orderNumber?.type === 'tryAgain' &&
                onlineErrors.orderNumberNotFound}
              {(['required', 'minLength', 'invalid', 'pattern'].includes(
                errors?.orderNumber?.type ?? 'invalid',
              ) ||
                inputError.orderNumber === 'error') &&
                onlineErrors.orderNumberInvalidFormat}
            </Typography>
          )}
        </FormController>
        <FormController
          className="sui-mt-6"
          fullWidth
          status={inputError.orderDate || (errors?.orderDate && 'error')}
        >
          <FormLabel htmlFor="orderDate">
            Order Date<sup>*</sup>
          </FormLabel>
          <Input
            aria-describedby="orderDate-input"
            fullWidth
            id="orderDate"
            inputProps={{
              maxLength: 8,
            }}
            {...register('orderDate', {
              required: true,
              minLength: 5,
              pattern: /^(\d{2})\/(\d{2})\/(\d{2})$/,
              onChange: (event: { target: { value: string } }) =>
                setValue('orderDate', addSlash(event.target.value)),
              onBlur: (event: { target: { value: string } }) =>
                setValue('orderDate', addSlash(event.target.value)),
            })}
            placeholder="MM/DD/YY"
          />
          {(inputError.orderDate === 'error' || errors.orderDate) && (
            <Typography variant="body-xs" color="danger">
              {errors?.orderDate?.type === 'tryAgain' &&
                onlineErrors.orderDateNotFound}
              {(['required', 'minLength', 'invalid', 'pattern'].includes(
                errors?.orderDate?.type ?? 'invalid',
              ) ||
                inputError.orderDate === 'error') &&
                onlineErrors.orderDateInvalidFormat}
            </Typography>
          )}
        </FormController>
        <FormController
          className="sui-mt-6"
          fullWidth
          status={inputError.email || (errors?.email && 'error')}
        >
          <FormLabel htmlFor="email">
            Email on Order<sup>*</sup>
          </FormLabel>
          <Input
            aria-describedby="email-input"
            fullWidth
            id="email"
            {...register('email', {
              required: true,
              minLength: 6,
              pattern: /^.+@[^.].*\.[a-z]{2,}$/,
              onChange: (event: { target: { value: string } }) =>
                setValue('email', event.target.value),
              onBlur: (event: { target: { value: string } }) =>
                setValue('email', event.target.value),
            })}
            placeholder="you@domain.com"
          />
          {(inputError.email === 'error' || errors.email) && (
            <Typography variant="body-xs" color="danger">
              {errors?.email?.type === 'tryAgain' && onlineErrors.emailNotFound}
              {(['required', 'minLength', 'invalid', 'pattern'].includes(
                errors?.email?.type ?? 'invalid',
              ) ||
                inputError.email === 'error') &&
                onlineErrors.emailInvalidFormat}
            </Typography>
          )}
        </FormController>
        {errors.tryAgain404 && (
          <div className="sui-mt-6">
            <Typography color="danger">{onlineErrors.api404Error}</Typography>
          </div>
        )}
        {errors.apiError && (
          <div className="sui-mt-6">
            <Typography color="danger">{errors?.apiError?.message}</Typography>
          </div>
        )}
      </DrawerBody>
      <AddReceiptDrawerFooter
        closeDrawer={closeDrawer}
        handleButtonClick={handleButtonClick}
        isLoading={isLoading}
        primaryButtonText="Add Online Receipt"
        reset={reset}
        secondaryButtonText="Cancel"
      />
    </div>
  );
};
